/**
 * Where the login is kept.
 *
 * The token lives in a cookie so the session survives the browser being closed
 * and reopened, and so it is still there on the next visit rather than only for
 * as long as the tab is around. The backend slides the expiry forward on every
 * /auth/me, so someone who opens the app regularly is never signed out.
 *
 * The cookie is deliberately readable by JavaScript: the API is called with an
 * `Authorization: Bearer` header, so the app itself has to be able to read the
 * value. That means this is no harder for a cross-site script to reach than
 * localStorage was - the gain here is persistence, not secrecy.
 */

const TOKEN_KEY = 'keventer_token';
const LEGACY_KEY = 'token';

// Matches the backend's ACCESS_TOKEN_EXPIRE_MINUTES (30 days). The cookie is
// rewritten with a full window every time a refreshed token arrives.
const MAX_AGE_DAYS = 30;

const isSecure = () =>
  typeof window !== 'undefined' && window.location?.protocol === 'https:';

export const readCookie = (name) => {
  if (typeof document === 'undefined') return null;
  const prefix = `${encodeURIComponent(name)}=`;
  const hit = document.cookie
    .split('; ')
    .find((row) => row.startsWith(prefix));
  if (!hit) return null;
  try {
    return decodeURIComponent(hit.slice(prefix.length));
  } catch (error) {
    return null;
  }
};

export const writeCookie = (name, value, days = MAX_AGE_DAYS) => {
  if (typeof document === 'undefined') return;
  const parts = [
    `${encodeURIComponent(name)}=${encodeURIComponent(value)}`,
    'path=/',
    `max-age=${Math.round(days * 24 * 60 * 60)}`,
    'SameSite=Lax',
  ];
  // Secure would make the cookie unwritable over plain http in local testing.
  if (isSecure()) parts.push('Secure');
  document.cookie = parts.join('; ');
};

export const clearCookie = (name) => {
  if (typeof document === 'undefined') return;
  const parts = [`${encodeURIComponent(name)}=`, 'path=/', 'max-age=0', 'SameSite=Lax'];
  if (isSecure()) parts.push('Secure');
  document.cookie = parts.join('; ');
};

const readMirror = () => {
  try {
    return localStorage.getItem(TOKEN_KEY) || localStorage.getItem(LEGACY_KEY);
  } catch (error) {
    return null;
  }
};

/**
 * A token's expiry, or 0 if it cannot be read. The signature is not checked -
 * only the server does that. This is purely to tell two stored copies apart.
 */
const expiryOf = (token) => {
  try {
    const payload = token.split('.')[1];
    if (!payload) return 0;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json).exp || 0;
  } catch (error) {
    return 0;
  }
};

/**
 * Read the stored token, adopting one left behind in localStorage by an earlier
 * build so nobody is signed out just because the storage moved.
 *
 * The two stores can disagree. Capacitor proxies `document.cookie` through native
 * storage, and on relaunch that can hand back an OLDER value than the one in
 * localStorage - sending it would present a superseded session id and the server
 * would rightly reject it. So the newer of the two wins, judged by expiry, and
 * the loser is overwritten. Every token is minted with the same lifetime, so a
 * later expiry means a later issue.
 */
export const getToken = () => {
  const fromCookie = readCookie(TOKEN_KEY);
  const fromMirror = readMirror();

  if (fromCookie && fromMirror && fromCookie !== fromMirror) {
    const winner = expiryOf(fromMirror) > expiryOf(fromCookie) ? fromMirror : fromCookie;
    writeCookie(TOKEN_KEY, winner);
    writeMirror(winner);
    return winner;
  }

  if (fromCookie) {
    if (fromMirror !== fromCookie) writeMirror(fromCookie);
    return fromCookie;
  }
  if (fromMirror) {
    writeCookie(TOKEN_KEY, fromMirror);
    // writeMirror also retires the old key, so an adopted legacy token is not
    // left lying about in storage under its previous name.
    writeMirror(fromMirror);
    return fromMirror;
  }
  return null;
};

function writeMirror(token) {
  try {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.removeItem(LEGACY_KEY);
  } catch (error) {
    // Storage can be unavailable; the cookie alone still carries the session.
  }
}

export const setToken = (token) => {
  writeCookie(TOKEN_KEY, token);
  writeMirror(token);
};

export const clearToken = () => {
  clearCookie(TOKEN_KEY);
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(LEGACY_KEY);
  } catch (error) {
    // A missing store means there is nothing to remove.
  }
};

/**
 * The last known profile, so a cold start can render the app straight away and
 * revalidate in the background instead of blocking on the network. Without this
 * an app opened before the radio is up has nothing to show but the login screen.
 *
 * This is a convenience copy, never an authority: every request is still
 * authorised server-side against the token.
 */
const USER_KEY = 'keventer_user';

export const getCachedUser = () => {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

export const setCachedUser = (user) => {
  try {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  } catch (error) {
    // Storage unavailable - the app just falls back to fetching the profile.
  }
};

export const clearCachedUser = () => {
  try {
    localStorage.removeItem(USER_KEY);
  } catch (error) {
    // Nothing to remove.
  }
};

export default { getToken, setToken, clearToken, getCachedUser, setCachedUser, clearCachedUser };
